/**
 * The handoff's product side: results rise into the card stage one by one,
 * staggered, so it reads as curation rather than a database dump. Built as
 * a short-lived overlay decoupled from ProductSwipeCard entirely — it
 * mimics that component's rest styling closely enough that swapping to the
 * real swipe stack once this finishes is invisible, but it owns none of the
 * swipe/drag/try-on machinery, so nothing about the real stack had to
 * change to make this work.
 */
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import useReducedMotion from '../../hooks/useReducedMotion';

const STAGGER_MS = 100;
const MAX_CARDS = 3;

export default function MahiResultsCascade({ products, onComplete }) {
  const reduced = useReducedMotion();
  const shown = products.slice(0, MAX_CARDS);

  useEffect(() => {
    const total = reduced ? 260 : shown.length * STAGGER_MS + 420;
    const timer = setTimeout(() => onComplete?.(), total);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);

  return (
    <div className="absolute inset-0" data-testid="mahi-results-cascade">
      {shown.map((product, stackIndex) => {
        const rest = { scale: 1 - stackIndex * 0.035, y: stackIndex * 14, opacity: 1 };
        return (
          <motion.div
            key={product.mira_sku}
            className="absolute inset-0"
            style={{ zIndex: 10 - stackIndex }}
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: rest.y + 46, scale: rest.scale }}
            animate={rest}
            transition={{
              duration: reduced ? 0.22 : 0.42,
              delay: (stackIndex * STAGGER_MS) / 1000,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <div className="w-full h-full relative overflow-hidden rounded-[1.75rem] bg-slate-200 shadow-[0_30px_70px_-25px_rgba(15,23,42,0.35)]">
              {product.image_urls?.[0] && (
                <img src={product.image_urls[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
              {stackIndex === 0 && (
                <div className="absolute inset-x-0 bottom-0 p-5 pb-6">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-white/70 font-semibold font-mahi-sans mb-1">
                    {product.brand}
                  </div>
                  <h3 className="font-mahi-serif text-2xl text-white leading-tight">
                    {product.display_title}
                  </h3>
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
